'use client';

import React, { useState, useEffect } from 'react';
import MemberDashboard from './MemberDashboard';
import ExpeditionBooking from './ExpeditionBooking';
import VaultAccess from './VaultAccess';
import MemberDirectory from './MemberDirectory';
import SecureMessaging from './SecureMessaging';
import Icon from '@/components/ui/AppIcon';

interface MemberPortalInteractiveProps {}

const MemberPortalInteractive = ({}: MemberPortalInteractiveProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    setIsHydrated(true);
    // Check if user is already authenticated (mock check)
    const authStatus = localStorage.getItem('pirateers_auth');
    if (authStatus === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:31:6" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="31" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:32:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="32" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-12%20w-12%20border-b-2%20border-accent%22%7D" className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>);

  }

  // Mock data
  const mockMemberProfile = {
    id: "member_001",
    name: "Alexander Sterling",
    memberSince: "2019",
    membershipTier: "Distinguished Elite",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c721f50e-1763292225873.png",
    alt: "Professional headshot of middle-aged man with salt and pepper beard in navy blazer",
    expeditionsCompleted: 23,
    vaultAccess: true,
    nextExpedition: "Ancient Silk Road Expedition - Departing March 15, 2025"
  };

  const mockRecentActivities = [
  {
    id: "activity_001",
    type: "expedition" as const,
    title: "Expedition Completed",
    description: "Successfully completed the Lost Temples of Cambodia expedition",
    timestamp: "2 hours ago",
    icon: "GlobeAltIcon"
  },
  {
    id: "activity_002",
    type: "vault" as const,
    title: "Vault Entry Added",
    description: "New archaeological site added to your personal collection",
    timestamp: "1 day ago",
    icon: "ArchiveBoxIcon"
  },
  {
    id: "activity_003",
    type: "message" as const,
    title: "New Message",
    description: "Message from Dr. Catherine Wells regarding upcoming expedition",
    timestamp: "2 days ago",
    icon: "ChatBubbleLeftRightIcon"
  }];


  const mockUpcomingExpeditions = [
  {
    id: "exp_001",
    title: "Ancient Silk Road Discovery",
    location: "Central Asia",
    duration: "14 days",
    difficulty: "Challenging" as const,
    availableSlots: 3,
    totalSlots: 8,
    startDate: "Mar 15, 2025",
    endDate: "Mar 29, 2025",
    price: "$12,500",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf768dc1-1766119006394.png",
    alt: "Ancient stone ruins along historic Silk Road with mountains in background",
    description: "Explore recently discovered archaeological sites along the historic Silk Road trade routes.",
    highlights: ["UNESCO World Heritage Sites", "Private archaeological access", "Expert historians"],
    requirements: ["Moderate fitness level", "Valid passport", "Travel insurance"]
  },
  {
    id: "exp_002",
    title: "Hidden Temples of Myanmar",
    location: "Myanmar",
    duration: "10 days",
    difficulty: "Moderate" as const,
    availableSlots: 5,
    totalSlots: 12,
    startDate: "Apr 8, 2025",
    endDate: "Apr 18, 2025",
    price: "$8,750",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1db49cbe0-1769493321433.png",
    alt: "Ancient Buddhist temple complex with golden pagodas surrounded by lush jungle",
    description: "Discover lesser-known temple complexes with exclusive access to restricted areas.",
    highlights: ["Private temple access", "Buddhist monastery visits", "Cultural immersion"],
    requirements: ["Basic fitness", "Respectful attire", "Cultural sensitivity"]
  }];


  const mockBookedExpeditions = [
  {
    id: "booked_001",
    title: "Arctic Research Expedition",
    location: "Svalbard, Norway",
    duration: "12 days",
    difficulty: "Expert" as const,
    availableSlots: 0,
    totalSlots: 6,
    startDate: "Feb 20, 2025",
    endDate: "Mar 4, 2025",
    price: "$18,500",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14309683b-1766094098336.png",
    alt: "Pristine Arctic landscape with ice formations and research station in distance",
    description: "Join climate researchers in studying Arctic ice formations and wildlife patterns.",
    highlights: ["Research collaboration", "Wildlife observation", "Ice cave exploration"],
    requirements: ["Excellent fitness", "Cold weather gear", "Research participation"]
  }];


  const mockVaultItems = [
  {
    id: "vault_001",
    title: "Lost City of Petra Extension",
    category: "Archaeological" as const,
    location: "Jordan",
    dateAdded: "Nov 10, 2024",
    accessLevel: "Elite" as const,
    image: "https://images.unsplash.com/photo-1714577745268-efa729c2ae4b",
    alt: "Ancient carved stone facade of Petra treasury building with intricate architectural details",
    description: "Recently discovered chambers beneath the famous Treasury building reveal new insights into Nabataean civilization.",
    coordinates: "30.3285° N, 35.4444° E",
    discoveredBy: "Dr. Sarah Mitchell",
    significance: "Redefines understanding of Nabataean urban planning"
  },
  {
    id: "vault_002",
    title: "Underwater Mayan Cenote",
    category: "Cultural" as const,
    location: "Yucatan, Mexico",
    dateAdded: "Nov 8, 2024",
    accessLevel: "Member" as const,
    image: "https://images.unsplash.com/photo-1734115581098-51dfc8edc762",
    alt: "Crystal clear underwater cenote with ancient Mayan artifacts visible on limestone floor",
    description: "Pristine cenote containing well-preserved Mayan ceremonial artifacts and offerings.",
    coordinates: "20.6843° N, 88.5678° W",
    discoveredBy: "Pirateers Diving Team",
    significance: "Intact ceremonial site with ritual significance"
  },
  {
    id: "vault_003",
    title: "Antarctic Research Station Alpha - Expedition Contract",
    category: "Historical" as const,
    location: "Antarctica",
    dateAdded: "Jan 15, 2025",
    accessLevel: "Restricted" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14af51bf6-1764917838599.png",
    alt: "Official document with wax seal and signatures on aged parchment paper",
    description: "Official expedition contract for the 2025 Antarctic deep ice core research mission. Includes liability waivers, equipment manifests, and research protocols.",
    coordinates: "75.5000° S, 0.0000° E",
    discoveredBy: "Legal Archives Division",
    significance: "Governs international research collaboration"
  },
  {
    id: "vault_004",
    title: "Silk Road Trade Route Mapping Project",
    category: "Historical" as const,
    location: "Central Asia",
    dateAdded: "Jan 12, 2025",
    accessLevel: "Elite" as const,
    image: "https://images.unsplash.com/photo-1730314737966-92b9760790eb",
    alt: "Detailed topographical map showing ancient Silk Road trade routes through mountain passes",
    description: "Comprehensive cartographic analysis of historical Silk Road routes with newly discovered waypoints and caravanserai locations.",
    coordinates: "41.3775° N, 64.5853° E",
    discoveredBy: "Prof. James Hartwell",
    significance: "Reveals previously unknown trade network connections"
  },
  {
    id: "vault_005",
    title: "Sunken Spanish Galleon - Nuestra Señora de Atocha",
    category: "Maritime" as const,
    location: "Florida Keys, USA",
    dateAdded: "Jan 10, 2025",
    accessLevel: "Member" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11351df5b-1769493321962.png",
    alt: "Underwater photograph of ancient shipwreck with coral-covered wooden hull and scattered artifacts",
    description: "Detailed survey and artifact catalog from the famous 1622 Spanish treasure fleet shipwreck site.",
    coordinates: "24.5465° N, 81.8043° W",
    discoveredBy: "Maritime Archaeology Unit",
    significance: "One of the richest shipwreck discoveries in history"
  },
  {
    id: "vault_006",
    title: "Amazon Rainforest Biodiversity Survey 2024",
    category: "Natural" as const,
    location: "Amazon Basin, Brazil",
    dateAdded: "Jan 8, 2025",
    accessLevel: "Member" as const,
    image: "https://images.unsplash.com/photo-1561389745-97596a161957",
    alt: "Dense Amazon rainforest canopy with mist rising through layers of green vegetation",
    description: "Comprehensive ecological survey documenting 247 previously unrecorded species including insects, amphibians, and plant life.",
    coordinates: "3.4653° S, 62.2159° W",
    discoveredBy: "Dr. Elena Rodriguez",
    significance: "Critical baseline data for conservation efforts"
  },
  {
    id: "vault_007",
    title: "Viking Settlement Excavation - Newfoundland",
    category: "Archaeological" as const,
    location: "L'Anse aux Meadows, Canada",
    dateAdded: "Jan 5, 2025",
    accessLevel: "Elite" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_187e3c564-1766135794827.png",
    alt: "Archaeological dig site showing Viking longhouse foundation stones and excavation grid",
    description: "New excavation reveals expanded Viking settlement with evidence of ironworking and extended occupation period.",
    coordinates: "51.5944° N, 55.5328° W",
    discoveredBy: "Dr. Magnus Eriksson",
    significance: "Extends timeline of Norse presence in North America"
  },
  {
    id: "vault_008",
    title: "Himalayan Expedition Permit & Route Map",
    category: "Historical" as const,
    location: "Nepal",
    dateAdded: "Jan 3, 2025",
    accessLevel: "Restricted" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16a5d2ff9-1769493321146.png",
    alt: "Official government permit document with topographical expedition route map overlay",
    description: "Official climbing permit and detailed route documentation for the 2025 Everest North Face expedition including weather windows and emergency protocols.",
    coordinates: "27.9881° N, 86.9250° E",
    discoveredBy: "Expedition Planning Office",
    significance: "Governs high-altitude research access"
  },
  {
    id: "vault_009",
    title: "Phoenician Port City - Underwater Survey",
    category: "Maritime" as const,
    location: "Mediterranean Coast, Lebanon",
    dateAdded: "Dec 28, 2024",
    accessLevel: "Elite" as const,
    image: "https://images.unsplash.com/photo-1596730886396-448db2972f1a",
    alt: "Underwater ruins of ancient stone harbor with visible columns and architectural remains",
    description: "Sonar mapping and photogrammetry of submerged Phoenician harbor complex dating to 1200 BCE.",
    coordinates: "33.2732° N, 35.2039° E",
    discoveredBy: "Mediterranean Maritime Institute",
    significance: "Reveals advanced ancient engineering techniques"
  },
  {
    id: "vault_010",
    title: "Galapagos Endemic Species Research Archive",
    category: "Natural" as const,
    location: "Galapagos Islands, Ecuador",
    dateAdded: "Dec 25, 2024",
    accessLevel: "Member" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11954c2d0-1767719881736.png",
    alt: "Galapagos giant tortoise in natural habitat with volcanic landscape background",
    description: "Longitudinal study tracking genetic diversity and population dynamics of endemic species across the archipelago.",
    coordinates: "0.9538° S, 90.9656° W",
    discoveredBy: "Dr. Isabella Darwin",
    significance: "Informs global conservation strategies"
  },
  {
    id: "vault_011",
    title: "Angkor Wat Temple Complex - LiDAR Survey",
    category: "Archaeological" as const,
    location: "Siem Reap, Cambodia",
    dateAdded: "Dec 22, 2024",
    accessLevel: "Elite" as const,
    image: "https://images.unsplash.com/photo-1621688023815-5fde13cf0e31",
    alt: "Aerial LiDAR visualization showing hidden temple structures beneath jungle canopy",
    description: "Revolutionary LiDAR mapping reveals extensive urban infrastructure and previously unknown temple complexes surrounding Angkor Wat.",
    coordinates: "13.4125° N, 103.8670° E",
    discoveredBy: "Dr. Sophia Chen",
    significance: "Rewrites understanding of Khmer Empire urban planning"
  },
  {
    id: "vault_012",
    title: "Private Collection Access Agreement - Medici Archives",
    category: "Historical" as const,
    location: "Florence, Italy",
    dateAdded: "Dec 20, 2024",
    accessLevel: "Restricted" as const,
    image: "https://images.unsplash.com/photo-1588072351557-1457a8ac6fcd",
    alt: "Ornate Renaissance-era document with gold leaf illumination and family crest",
    description: "Exclusive access contract to the private Medici family archives including correspondence, financial records, and artistic commissions from 1400-1600.",
    coordinates: "43.7696° N, 11.2558° E",
    discoveredBy: "Heritage Partnerships Division",
    significance: "Unprecedented access to Renaissance primary sources"
  },
  {
    id: "vault_013",
    title: "Pacific Ring of Fire Volcanic Activity Map",
    category: "Natural" as const,
    location: "Pacific Ocean",
    dateAdded: "Dec 18, 2024",
    accessLevel: "Member" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10f3d2cd5-1765213994221.png",
    alt: "Detailed geological map showing active volcanic zones and tectonic plate boundaries",
    description: "Real-time seismic monitoring data and predictive modeling for volcanic activity across the Pacific Ring of Fire.",
    coordinates: "Various locations",
    discoveredBy: "Geological Survey Team",
    significance: "Critical for expedition safety planning"
  },
  {
    id: "vault_014",
    title: "Ottoman Empire Naval Charts Collection",
    category: "Maritime" as const,
    location: "Istanbul, Turkey",
    dateAdded: "Dec 15, 2024",
    accessLevel: "Elite" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c514700d-1769493321214.png",
    alt: "Historical Ottoman naval chart with Arabic calligraphy and coastal navigation details",
    description: "Digitized collection of 16th-century Ottoman naval charts showing Mediterranean and Black Sea trade routes and naval bases.",
    coordinates: "41.0082° N, 28.9784° E",
    discoveredBy: "Naval History Archive",
    significance: "Reveals Ottoman maritime dominance strategies"
  },
  {
    id: "vault_015",
    title: "Machu Picchu Astronomical Alignment Study",
    category: "Cultural" as const,
    location: "Cusco Region, Peru",
    dateAdded: "Dec 12, 2024",
    accessLevel: "Member" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1630d4a62-1767719875820.png",
    alt: "Machu Picchu ruins at sunrise with precise astronomical alignment markers highlighted",
    description: "Comprehensive analysis of Incan astronomical knowledge encoded in Machu Picchu\'s architectural alignments and ceremonial spaces.",
    coordinates: "13.1631° S, 72.5450° W",
    discoveredBy: "Dr. Carlos Mendoza",
    significance: "Demonstrates advanced pre-Columbian astronomy"
  },
  {
    id: "vault_016",
    title: "Arctic Ice Core Climate Data - 800,000 Years",
    category: "Natural" as const,
    location: "Greenland Ice Sheet",
    dateAdded: "Dec 10, 2024",
    accessLevel: "Elite" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10c6a2e51-1765599070168.png",
    alt: "Cylindrical ice core sample showing distinct annual layers and trapped air bubbles",
    description: "Complete ice core analysis providing 800,000 years of climate history including atmospheric composition and temperature fluctuations.",
    coordinates: "72.5796° N, 38.4592° W",
    discoveredBy: "International Ice Core Consortium",
    significance: "Longest continuous climate record available"
  },
  {
    id: "vault_017",
    title: "Pompeii Villa Excavation Contract",
    category: "Archaeological" as const,
    location: "Pompeii, Italy",
    dateAdded: "Dec 8, 2024",
    accessLevel: "Restricted" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_115f8e5c6-1769493319918.png",
    alt: "Official Italian archaeological permit with ministry stamps and excavation grid coordinates",
    description: "Official excavation permit and partnership agreement for the Villa of the Mysteries restoration and research project.",
    coordinates: "40.7489° N, 14.4897° E",
    discoveredBy: "Archaeological Permits Office",
    significance: "Governs preservation of UNESCO World Heritage Site"
  },
  {
    id: "vault_018",
    title: "Titanic Wreck Site Documentation",
    category: "Maritime" as const,
    location: "North Atlantic Ocean",
    dateAdded: "Dec 5, 2024",
    accessLevel: "Elite" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb7b9296-1769493320998.png",
    alt: "Deep sea submersible photograph of Titanic bow section covered in rusticles",
    description: "High-resolution photogrammetry and 3D modeling of the RMS Titanic wreck site at 12,500 feet depth.",
    coordinates: "41.7325° N, 49.9469° W",
    discoveredBy: "Deep Ocean Exploration Team",
    significance: "Monitors deterioration of historic wreck site"
  },
  {
    id: "vault_019",
    title: "Sahara Desert Ancient Lake Beds Survey",
    category: "Natural" as const,
    location: "Sahara Desert, North Africa",
    dateAdded: "Dec 3, 2024",
    accessLevel: "Member" as const,
    image: "https://images.unsplash.com/photo-1451186242394-2b461812025b",
    alt: "Satellite imagery showing ancient dried lake bed patterns in Sahara Desert",
    description: "Satellite and ground survey revealing extensive network of ancient lake systems that supported early human migration.",
    coordinates: "23.4162° N, 25.6628° E",
    discoveredBy: "Dr. Amara Okafor",
    significance: "Explains human migration patterns out of Africa"
  },
  {
    id: "vault_020",
    title: "Easter Island Moai Quarry Research",
    category: "Cultural" as const,
    location: "Rapa Nui, Chile",
    dateAdded: "Nov 30, 2024",
    accessLevel: "Elite" as const,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b893187-1769493321936.png",
    alt: "Partially carved moai statue still attached to volcanic rock quarry face",
    description: "Archaeological investigation of Rano Raraku quarry revealing moai construction techniques and abandonment timeline.",
    coordinates: "27.1127° S, 109.3497° W",
    discoveredBy: "Dr. Hotu Matua",
    significance: "Solves mystery of moai construction methods"
  }];



  const mockVaultCollections = [
  {
    id: "collection_001",
    name: "Ancient Maritime Routes",
    itemCount: 47,
    description: "Comprehensive collection of historical shipping lanes and underwater archaeological sites.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae61073a-1766153975483.png",
    alt: "Vintage nautical map showing ancient trade routes across Mediterranean Sea",
    accessLevel: "Elite"
  },
  {
    id: "collection_002",
    name: "Lost Civilizations",
    itemCount: 32,
    description: "Archaeological sites and artifacts from civilizations lost to time.",
    image: "https://images.unsplash.com/photo-1671624187402-360870441994",
    alt: "Ancient stone ruins partially covered by jungle vegetation with mysterious carvings",
    accessLevel: "Member"
  },
  {
    id: "collection_003",
    name: "Expedition Contracts & Legal Documents",
    itemCount: 89,
    description: "Official permits, access agreements, and partnership contracts for global research expeditions.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12079f8f0-1769197886577.png",
    alt: "Stack of official documents with wax seals and government stamps",
    accessLevel: "Restricted"
  },
  {
    id: "collection_004",
    name: "Climate & Geological Archives",
    itemCount: 156,
    description: "Ice core data, seismic surveys, and long-term environmental monitoring records.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eef4e1fe-1766726351852.png",
    alt: "Scientific ice core samples and geological survey equipment in laboratory setting",
    accessLevel: "Member"
  },
  {
    id: "collection_005",
    name: "Shipwreck Documentation",
    itemCount: 73,
    description: "Detailed surveys and artifact catalogs from historic maritime disaster sites worldwide.",
    image: "https://images.unsplash.com/photo-1652137568397-462161c35858",
    alt: "Underwater photograph of historic shipwreck with scattered artifacts on ocean floor",
    accessLevel: "Elite"
  },
  {
    id: "collection_006",
    name: "Private Archive Access Agreements",
    itemCount: 24,
    description: "Exclusive contracts granting access to royal, institutional, and family archives globally.",
    image: "https://images.unsplash.com/photo-1588072351557-1457a8ac6fcd",
    alt: "Ornate historical document with gold leaf decoration and official seals",
    accessLevel: "Restricted"
  },
  {
    id: "collection_007",
    name: "Biodiversity Surveys & Species Catalogs",
    itemCount: 112,
    description: "Comprehensive ecological surveys documenting flora and fauna in remote ecosystems.",
    image: "https://images.unsplash.com/photo-1694230896211-f990ae81163e",
    alt: "Dense tropical rainforest canopy with diverse plant species and wildlife",
    accessLevel: "Member"
  },
  {
    id: "collection_008",
    name: "Historical Cartography Collection",
    itemCount: 198,
    description: "Rare maps, navigation charts, and geographic surveys from 15th-20th centuries.",
    image: "https://images.unsplash.com/photo-1550406307-84b491d68ba7",
    alt: "Antique world map with hand-colored regions and decorative compass rose",
    accessLevel: "Elite"
  },
  {
    id: "collection_009",
    name: "UNESCO World Heritage Research",
    itemCount: 67,
    description: "Authorized research documentation from protected archaeological and cultural sites.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_163272dd0-1769493322497.png",
    alt: "Machu Picchu archaeological site with research equipment and documentation",
    accessLevel: "Elite"
  },
  {
    id: "collection_010",
    name: "Deep Ocean Exploration",
    itemCount: 54,
    description: "Submersible surveys, hydrothermal vent studies, and abyssal plain documentation.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d54e8c3b-1766612954902.png",
    alt: "Deep sea submersible illuminating ocean floor with bioluminescent organisms",
    accessLevel: "Member"
  }];



  const mockMembers = [
  {
    id: "member_002",
    name: "Dr. Catherine Wells",
    title: "Archaeological Researcher",
    location: "Oxford, UK",
    memberSince: "2017",
    expeditionsCompleted: 31,
    specialties: ["Ancient Civilizations", "Maritime Archaeology", "Cultural Preservation"],
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165f34e4f-1763296821528.png",
    alt: "Professional portrait of woman with brown hair in academic setting wearing glasses",
    isOnline: true,
    lastActive: "2 minutes ago",
    membershipTier: "Distinguished" as const,
    privacyLevel: "Members Only" as const
  },
  {
    id: "member_003",
    name: "Captain James Morrison",
    title: "Maritime Explorer",
    location: "Sydney, Australia",
    memberSince: "2018",
    expeditionsCompleted: 28,
    specialties: ["Deep Sea Exploration", "Shipwreck Investigation", "Marine Biology"],
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_158643af8-1766219556529.png",
    alt: "Weathered sea captain with gray beard wearing nautical jacket on ship deck",
    isOnline: false,
    lastActive: "1 hour ago",
    membershipTier: "Elite" as const,
    privacyLevel: "Public" as const
  }];


  const mockOnlineMembers = mockMembers.filter((member) => member.isOnline);

  const mockConversations = [
  {
    id: "conv_001",
    participantName: "Dr. Catherine Wells",
    participantAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165f34e4f-1763296821528.png",
    participantAlt: "Professional portrait of woman with brown hair in academic setting wearing glasses",
    lastMessage: "The archaeological permits for the Jordan expedition have been approved.",
    lastMessageTime: "2m",
    unreadCount: 2,
    isOnline: true
  },
  {
    id: "conv_002",
    participantName: "Captain James Morrison",
    participantAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_158643af8-1766219556529.png",
    participantAlt: "Weathered sea captain with gray beard wearing nautical jacket on ship deck",
    lastMessage: "Weather conditions look perfect for the dive expedition next week.",
    lastMessageTime: "1h",
    unreadCount: 0,
    isOnline: false
  }];


  const mockMessages = [
  {
    id: "msg_001",
    senderId: "member_002",
    senderName: "Dr. Catherine Wells",
    senderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165f34e4f-1763296821528.png",
    senderAlt: "Professional portrait of woman with brown hair in academic setting wearing glasses",
    content: "Hello Alexander! I wanted to update you on the Jordan expedition permits.",
    timestamp: "10:30 AM",
    isRead: true,
    isFromCurrentUser: false
  },
  {
    id: "msg_002",
    senderId: "member_001",
    senderName: "Alexander Sterling",
    senderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c721f50e-1763292225873.png",
    senderAlt: "Professional headshot of middle-aged man with salt and pepper beard in navy blazer",
    content: "Excellent news! When do we expect to receive the final documentation?",
    timestamp: "10:32 AM",
    isRead: true,
    isFromCurrentUser: true
  },
  {
    id: "msg_003",
    senderId: "member_002",
    senderName: "Dr. Catherine Wells",
    senderAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_165f34e4f-1763296821528.png",
    senderAlt: "Professional portrait of woman with brown hair in academic setting wearing glasses",
    content: "The archaeological permits for the Jordan expedition have been approved. We should have everything by Friday.",
    timestamp: "10:35 AM",
    isRead: false,
    isFromCurrentUser: false
  }];


  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();

    // Mock authentication - check against mock credentials
    if (loginForm.email === 'alexander.sterling@pirateers.com' && loginForm.password === 'EliteAccess2024!') {
      setIsAuthenticated(true);
      setLoginError('');
      localStorage.setItem('pirateers_auth', 'authenticated');
    } else {
      setLoginError('Invalid credentials. Use: alexander.sterling@pirateers.com / EliteAccess2024!');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('pirateers_auth');
    setActiveTab('dashboard');
  };

  const tabs = [
  { id: 'dashboard', name: 'Dashboard', icon: 'HomeIcon' },
  { id: 'expeditions', name: 'Expeditions', icon: 'GlobeAltIcon' },
  { id: 'vault', name: 'The Vault', icon: 'ArchiveBoxIcon' },
  { id: 'directory', name: 'Directory', icon: 'UsersIcon' },
  { id: 'messages', name: 'Messages', icon: 'ChatBubbleLeftRightIcon' }];


  if (!isAuthenticated) {
    return (
      <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:635:6" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="635" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%20px-4%22%7D" className="min-h-screen bg-background flex items-center justify-center px-4">
        <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:636:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="636" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-md%20w-full%22%7D" className="max-w-md w-full">
          <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:637:10" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="637" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20shadow-institutional%20border%20border-border%20p-8%22%7D" className="bg-card rounded-xl shadow-institutional border border-border p-8">
            <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:638:12" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="638" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-8%22%7D" className="text-center mb-8">
              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:639:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="639" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20bg-accent%2F10%20rounded-full%20flex%20items-center%20justify-center%20mx-auto%20mb-4%22%7D" className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:640:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="640" data-component-file="MemberPortalInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22LockClosedIcon%22%2C%22className%22%3A%22text-accent%22%7D" name="LockClosedIcon" size={32} className="text-accent" />
              </div>
              <h1 data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:642:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="642" data-component-file="MemberPortalInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-2xl%20font-playfair%20font-bold%20text-primary%20mb-2%22%2C%22textContent%22%3A%22Member%20Portal%20Access%22%7D" className="text-2xl font-playfair font-bold text-primary mb-2">
                Member Portal Access
              </h1>
              <p data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:645:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="645" data-component-file="MemberPortalInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%22%2C%22textContent%22%3A%22Secure%20login%20for%20Pirateers%20Elite%20members%22%7D" className="text-text-secondary font-inter">
                Secure login for Pirateers Elite members
              </p>
            </div>

            <form data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:650:12" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="650" data-component-file="MemberPortalInteractive.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-6%22%7D" onSubmit={handleLogin} className="space-y-6">
              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:651:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="651" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:652:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="652" data-component-file="MemberPortalInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-inter%20font-semibold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Email%20Address%22%7D" className="block text-sm font-inter font-semibold text-text-primary mb-2">
                  Email Address
                </label>
                <input data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:655:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="655" data-component-file="MemberPortalInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20bg-input%20border%20border-border%20rounded-lg%20font-inter%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent%22%7D"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg font-inter focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Enter your email"
                  required />

              </div>

              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:665:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="665" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:666:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="666" data-component-file="MemberPortalInteractive.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-inter%20font-semibold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Password%22%7D" className="block text-sm font-inter font-semibold text-text-primary mb-2">
                  Password
                </label>
                <input data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:669:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="669" data-component-file="MemberPortalInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22password%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20bg-input%20border%20border-border%20rounded-lg%20font-inter%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-accent%22%7D"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg font-inter focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Enter your password"
                  required />

              </div>

              {loginError &&
              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:680:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="680" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-3%20bg-destructive%2F10%20border%20border-destructive%2F20%20rounded-lg%22%7D" className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:681:18" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="681" data-component-file="MemberPortalInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-destructive%20font-inter%22%7D" className="text-sm text-destructive font-inter">{loginError}</p>
                </div>
              }

              <button data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:685:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="685" data-component-file="MemberPortalInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%2C%22className%22%3A%22w-full%20px-6%20py-3%20bg-accent%20hover%3Abg-heritage-bronze%20text-white%20font-inter%20font-semibold%20rounded-lg%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22Access%20Portal%22%7D"
                type="submit"
                className="w-full px-6 py-3 bg-accent hover:bg-heritage-bronze text-white font-inter font-semibold rounded-lg transition-colors duration-200">

                Access Portal
              </button>
            </form>

            <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:693:12" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="693" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20pt-6%20border-t%20border-border%22%7D" className="mt-6 pt-6 border-t border-border">
              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:694:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="694" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-muted%2F50%20rounded-lg%20p-4%22%7D" className="bg-muted/50 rounded-lg p-4">
                <h3 data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:695:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="695" data-component-file="MemberPortalInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-inter%20font-semibold%20text-text-primary%20mb-2%20text-sm%22%2C%22textContent%22%3A%22Demo%20Credentials%3A%22%7D" className="font-inter font-semibold text-text-primary mb-2 text-sm">
                  Demo Credentials:
                </h3>
                <p data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:698:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="698" data-component-file="MemberPortalInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-text-secondary%20font-inter%22%2C%22textContent%22%3A%22Email%3A%20alexander.sterling%40pirateers.com%20Password%3A%20EliteAccess2024!%22%7D" className="text-xs text-text-secondary font-inter">
                  Email: alexander.sterling@pirateers.com<br data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:699:57" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="699" data-component-file="MemberPortalInteractive.tsx" data-component-name="br" data-component-content="%7B%22elementName%22%3A%22br%22%7D" />
                  Password: EliteAccess2024!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:711:4" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="711" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      {/* Portal Header */}
      <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:713:6" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="713" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border-b%20border-border%20sticky%20top-16%20z-40%22%7D" className="bg-card border-b border-border sticky top-16 z-40">
        <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:714:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="714" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
          <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:715:10" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="715" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-16%22%7D" className="flex items-center justify-between h-16">
            <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:716:12" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="716" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-8%22%7D" className="flex items-center space-x-8">
              <h1 data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:717:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="717" data-component-file="MemberPortalInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-xl%20font-playfair%20font-semibold%20text-primary%22%2C%22textContent%22%3A%22Member%20Portal%22%7D" className="text-xl font-playfair font-semibold text-primary">
                Member Portal
              </h1>
              <nav data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:720:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="720" data-component-file="MemberPortalInteractive.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20md%3Aflex%20items-center%20space-x-6%22%7D" className="hidden md:flex items-center space-x-6">
                {tabs.map((tab) =>
                <button data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:722:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="722" data-component-file="MemberPortalInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-inter font-medium transition-colors duration-200 ${
                  activeTab === tab.id ?
                  'bg-accent/10 text-accent' : 'text-text-secondary hover:text-accent hover:bg-muted'}`
                  }>

                    <Icon data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:730:20" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="730" data-component-file="MemberPortalInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={tab.icon as any} size={20} />
                    <span data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:731:20" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="731" data-component-file="MemberPortalInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{tab.name}</span>
                  </button>
                )}
              </nav>
            </div>
            <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:736:12" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="736" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-4%22%7D" className="flex items-center space-x-4">
              <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:737:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="737" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
                <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:738:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="738" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20rounded-full%20overflow-hidden%22%7D" className="w-8 h-8 rounded-full overflow-hidden">
                  <img data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:739:18" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="739" data-component-file="MemberPortalInteractive.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
                    src={mockMemberProfile.avatar}
                    alt={mockMemberProfile.alt}
                    className="w-full h-full object-cover" />

                </div>
                <span data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:745:16" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="745" data-component-file="MemberPortalInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20md%3Ablock%20font-inter%20font-medium%20text-text-primary%22%7D" className="hidden md:block font-inter font-medium text-text-primary">
                  {mockMemberProfile.name}
                </span>
              </div>
              <button data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:749:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="749" data-component-file="MemberPortalInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-4%20py-2%20bg-muted%20hover%3Abg-destructive%2F10%20text-text-secondary%20hover%3Atext-destructive%20font-inter%20font-medium%20rounded-lg%20transition-colors%20duration-200%22%2C%22textContent%22%3A%22Logout%22%7D"
                onClick={handleLogout}
                className="px-4 py-2 bg-muted hover:bg-destructive/10 text-text-secondary hover:text-destructive font-inter font-medium rounded-lg transition-colors duration-200">

                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tab Navigation */}
      <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:761:6" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="761" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22md%3Ahidden%20bg-card%20border-b%20border-border%22%7D" className="md:hidden bg-card border-b border-border">
        <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:762:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="762" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20overflow-x-auto%22%7D" className="flex overflow-x-auto">
          {tabs.map((tab) =>
          <button data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:764:10" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="764" data-component-file="MemberPortalInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 whitespace-nowrap font-inter font-medium transition-colors duration-200 ${
            activeTab === tab.id ?
            'bg-accent/10 text-accent border-b-2 border-accent' : 'text-text-secondary hover:text-accent'}`
            }>

              <Icon data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:772:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="772" data-component-file="MemberPortalInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={tab.icon as any} size={20} />
              <span data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:773:14" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="773" data-component-file="MemberPortalInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{tab.name}</span>
            </button>
          )}
        </div>
      </div>

      {/* Portal Content */}
      <div data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:780:6" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="780" data-component-file="MemberPortalInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%20py-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' &&
        <MemberDashboard data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:782:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="782" data-component-file="MemberPortalInteractive.tsx" data-component-name="MemberDashboard" data-component-content="%7B%22elementName%22%3A%22MemberDashboard%22%7D"
          memberProfile={mockMemberProfile}
          recentActivities={mockRecentActivities} />

        }
        {activeTab === 'expeditions' &&
        <ExpeditionBooking data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:788:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="788" data-component-file="MemberPortalInteractive.tsx" data-component-name="ExpeditionBooking" data-component-content="%7B%22elementName%22%3A%22ExpeditionBooking%22%7D"
          upcomingExpeditions={mockUpcomingExpeditions}
          bookedExpeditions={mockBookedExpeditions} />

        }
        {activeTab === 'vault' &&
        <VaultAccess data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:794:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="794" data-component-file="MemberPortalInteractive.tsx" data-component-name="VaultAccess" data-component-content="%7B%22elementName%22%3A%22VaultAccess%22%7D"
          recentItems={mockVaultItems}
          featuredCollections={mockVaultCollections} />

        }
        {activeTab === 'directory' &&
        <MemberDirectory data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:800:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="800" data-component-file="MemberPortalInteractive.tsx" data-component-name="MemberDirectory" data-component-content="%7B%22elementName%22%3A%22MemberDirectory%22%7D"
          members={mockMembers}
          onlineMembers={mockOnlineMembers} />

        }
        {activeTab === 'messages' &&
        <SecureMessaging data-component-id="src/app/member-portal/components/MemberPortalInteractive.tsx:806:8" data-component-path="src/app/member-portal/components/MemberPortalInteractive.tsx" data-component-line="806" data-component-file="MemberPortalInteractive.tsx" data-component-name="SecureMessaging" data-component-content="%7B%22elementName%22%3A%22SecureMessaging%22%7D"
          conversations={mockConversations}
          messages={mockMessages} />

        }
      </div>
    </div>);

};

export default MemberPortalInteractive;